import { useState, useEffect, useCallback } from 'react';
import { HistoryEntry, HistoryEntryType } from '../../types/ownerHistory';
import { getHistory } from './HistoryDrawerApiHelper';
import { calculateTimeSpent, formatDateByFormat, DATE_FORMATS } from '../../utils/dateUtils';
import { HISTORY_DRAWER_DISPLAY } from './constants';

export interface UseHistoryDrawerProps {
    referenceId: string;
    fieldKey: string;
    open: boolean;
}

export interface UseHistoryDrawerResult {
    entries: HistoryEntry[];
    loading: boolean;
    error: boolean;
    resolveUserName: (userId: string, userList: { userId: string; name?: string }[]) => string;
    getEntryMainLabel: (entry: HistoryEntry, userList?: { userId: string; name?: string }[]) => string;
    toDisplayValue: (value: string | null | undefined) => string;
    formatEntryDate: (epoch: number) => string;
    getTimeSpent: (entry: HistoryEntry, nextEntry?: HistoryEntry) => string;
}

// Treat null/undefined AND empty/whitespace strings as "None" — a removed owner is
// persisted with an empty value, which `?? 'None'` would not catch (renders a blank chip).
// Multi-select values arrive as an array — join non-empty members.
export const toDisplayValue = (value: string | string[] | null | undefined): string => {
    if (Array.isArray(value)) {
        const joined = value.filter((v) => v && String(v).trim()).join(', ');
        return joined || HISTORY_DRAWER_DISPLAY.EMPTY_VALUE_DISPLAY;
    }
    return value && value.trim() ? value : HISTORY_DRAWER_DISPLAY.EMPTY_VALUE_DISPLAY;
};

export const resolveUserName = (userId: string, userList: { userId: string; name?: string }[]): string => {
    if (!userId) return HISTORY_DRAWER_DISPLAY.SYSTEM_USER_FALLBACK;
    const user = userList.find((u) => u.userId === userId);
    return user?.name ?? userId;
};

// For OWNER_UPDATE the stored value can be a sort key rather than a display name — e.g. the
// "Not Assigned" pseudo-user is persisted via its customSortName ("AAAAAAAAAAA") to float it to
// the top of the owner dropdown. Resolve the human name from the userId via the owner list, and
// fall back to the stored value when the user isn't in the list (e.g. a deactivated owner).
const resolveOwnerLabel = (
    userId: string | null | undefined,
    storedValue: string | string[] | null | undefined,
    userList: { userId: string; name?: string }[],
): string => {
    const match = userId ? userList.find((u) => u.userId === userId) : undefined;
    return match?.name ? match.name : toDisplayValue(storedValue);
};

export const getEntryMainLabel = (entry: HistoryEntry, userList: { userId: string; name?: string }[] = []): string => {
    switch (entry.type as HistoryEntryType) {
        case 'STATUS_UPDATE':
        case 'CUSTOM_FIELD_UPDATE':
            // Show only the latest value (not a "previous → current" transition),
            // consistent with owner history which shows just the current assignee.
            return toDisplayValue(entry.currentValue);
        case 'OWNER_UPDATE':
        default:
            return resolveOwnerLabel(entry.currentUserId, entry.currentValue, userList);
    }
};

export const formatEntryDate = (epoch: number): string => formatDateByFormat(epoch, DATE_FORMATS.HISTORY_DRAWER);

export const getTimeSpent = (entry: HistoryEntry, nextEntry?: HistoryEntry): string =>
    calculateTimeSpent(entry.changeDate, nextEntry?.changeDate);

/**
 * Manages async fetch of owner/field history entries for a given reference + field key.
 * Resets state when the drawer closes and aborts in-flight requests on unmount or
 * re-open to prevent stale-update races.
 */
export const useHistoryDrawer = ({ referenceId, fieldKey, open }: UseHistoryDrawerProps): UseHistoryDrawerResult => {
    const [entries, setEntries] = useState<HistoryEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    /**
     * Kicks off the history fetch for the current reference + field key, flipping
     * loading on and clearing any prior error. Declared outside the effect (memoised
     * on its inputs) so the effect simply invokes it and the callers in tests can too.
     */
    const fetchHistory = useCallback(
        (onResult: (result: HistoryEntry[] | false) => void) => {
            setLoading(true);
            setError(false);

            getHistory({ referenceId, key: fieldKey }).then(onResult);
        },
        [referenceId, fieldKey],
    );

    useEffect(() => {
        if (!open) {
            setEntries([]);
            setError(false);
            return;
        }

        if (!referenceId || !fieldKey) return;

        let cancelled = false;

        fetchHistory((result) => {
            if (cancelled) return;

            if (result !== false) {
                setEntries(result);
            } else {
                setEntries([]);
                setError(true);
            }

            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [open, referenceId, fieldKey, fetchHistory]);

    return {
        entries,
        loading,
        error,
        resolveUserName,
        getEntryMainLabel,
        toDisplayValue,
        formatEntryDate,
        getTimeSpent,
    };
};
